'use client'

import { PricingCard } from '../components/PricingCard'
import { pricingSectionBlockVariants } from '../constants/pricing-section-motion'
import type { CommonLocale, CommonTranslations } from '../types/site-copy'
import { Badge } from '@tailark/core/ui/badge'
import { AnimatedGroup } from '@tailark/core/ui/animated-group'
import { Label } from '@tailark/core/ui/label'
import { Switch } from '@tailark/core/ui/switch'
import { Building2, Clover, Gem } from 'lucide-react'
import { useState } from 'react'

interface CompareFeatureRow {
    name: string
    free: boolean
    pro: boolean
    business: boolean
}

function CompareCell({ included }: { included: boolean }) {
    return (
        <div className="border-border flex h-14 items-center justify-center border-t px-4 md:px-6">
            {included ? (
                <span className="flex size-5 items-center justify-center rounded-full bg-emerald-500/10 font-sans text-xs font-semibold text-emerald-600">
                    ✓
                </span>
            ) : (
                <span className="bg-foreground/[0.065] text-foreground/65 flex size-5 items-center justify-center rounded-full font-sans text-xs font-semibold">
                    ✗
                </span>
            )}
        </div>
    )
}

export default function PricingSectionCards({
    locale,
    translations,
}: {
    locale: CommonLocale
    translations: CommonTranslations
}) {
    const t = translations
    const isPersian = locale === 'fa'
    const usdToTomanRate = 150000
    const planFeatureGroups: { features: CompareFeatureRow[] }[] = [
        {
            features: [
                { name: t.pricingCards.featureList.removeBranding, free: false, pro: true, business: true },
                { name: t.pricingCards.featureList.customDomains, free: false, pro: true, business: true },
                { name: t.pricingCards.featureList.advancedCustomization, free: false, pro: true, business: true },
                { name: t.pricingCards.featureList.customCSS, free: false, pro: true, business: true },
                { name: t.pricingCards.featureList.customizeLinkPreview, free: false, pro: true, business: true },
            ],
        },
        {
            features: [
                {
                    name: t.pricingCards.featureList.collaborationUnlimited,
                    free: false,
                    pro: true,
                    business: true,
                },
                { name: t.pricingCards.featureList.workspaces, free: false, pro: true, business: true },
            ],
        },
        {
            features: [
                { name: t.pricingCards.featureList.partialSubmissions, free: false, pro: true, business: true },
                { name: t.pricingCards.featureList.unlimitedUploads, free: false, pro: true, business: true },
                { name: t.pricingCards.featureList.formVisitAnalytics, free: false, pro: true, business: true },
                { name: t.pricingCards.featureList.dropOffAnalytics, free: false, pro: true, business: true },
                { name: t.pricingCards.featureList.premiumIntegrations, free: false, pro: true, business: true },
            ],
        },
        {
            features: [
                { name: t.pricingCards.featureList.emailNotifications, free: false, pro: true, business: true },
                { name: t.pricingCards.featureList.customEmailDomains, free: false, pro: true, business: true },
                { name: t.pricingCards.featureList.verifyEmails, free: false, pro: false, business: true },
            ],
        },
        {
            features: [
                { name: t.pricingCards.featureList.controlDataRetention, free: false, pro: false, business: true },
                { name: t.pricingCards.featureList.versionHistory30Days, free: false, pro: true, business: false },
                { name: t.pricingCards.featureList.versionHistory90Days, free: false, pro: false, business: true },
            ],
        },
        {
            features: [
                { name: t.pricingCards.featureList.everythingInPro, free: false, pro: false, business: true },
                { name: t.pricingCards.featureList.moreToCome, free: false, pro: false, business: true },
            ],
        },
    ]

    const compareRows = planFeatureGroups.flatMap((group) => group.features.map((f) => ({ ...f })))

    const [yearly, setYearly] = useState(false)
    const proPrice = yearly ? 29 * 0.9 : 29
    const businessPrice = yearly ? 89 * 0.9 : 89
    const toToman = (usdAmount: number): number => Math.round(usdAmount * usdToTomanRate)
    const formatToman = (amount: number): string => `${new Intl.NumberFormat('fa-IR').format(amount)}`
    const formatPriceValue = (usdAmount: number): string =>
        isPersian ? formatToman(toToman(usdAmount)) : `${Math.round(usdAmount)}`
    const currencyLabel = isPersian ? 'تومان' : 'USD'
    const proDisplay = formatPriceValue(proPrice)
    const businessDisplay = yearly ? formatPriceValue(businessPrice) : formatPriceValue(89)
    const proPeriodAmount = yearly ? Math.round(proPrice) * 12 : Math.round(proPrice)
    const businessPeriodAmount = Math.round(businessPrice) * (yearly ? 12 : 1)
    const proSubtitleAmount = isPersian ? `${formatToman(toToman(proPeriodAmount))} تومان` : `$${proPeriodAmount}`
    const businessSubtitleAmount = isPersian
        ? `${formatToman(toToman(businessPeriodAmount))} تومان`
        : `$${businessPeriodAmount}`
    const periodLabel = yearly ? t.pricingCards.perYear : t.pricingCards.perMonth

    return (
        <div className="container mx-auto px-4 py-24 md:px-6 lg:py-32 2xl:max-w-[1400px]">
            <AnimatedGroup
                variants={pricingSectionBlockVariants}
                className="flex flex-col">
                <div className="mx-auto mb-10 max-w-2xl text-center lg:mb-14">
                    <h2 className="text-h2 mt-8">{t.pricingCards.title}</h2>
                    <p className="text-muted-foreground mt-1">{t.pricingCards.subtitle}</p>
                </div>

                <div className="flex items-center justify-center">
                    <Label
                        htmlFor="payment-schedule"
                        className="me-3">
                        {t.pricingCards.payMonthly}
                    </Label>
                    <Switch
                        id="payment-schedule"
                        checked={yearly}
                        onCheckedChange={setYearly}
                    />
                    <Label
                        htmlFor="payment-schedule"
                        className="relative ms-3">
                        {t.pricingCards.payYearly}
                        <span className="absolute -end-28 -top-10 start-auto">
                            <span className="flex items-center">
                                <Badge className="mt-3 uppercase">{t.pricingCards.saveUpTo}</Badge>
                            </span>
                        </span>
                    </Label>
                </div>

                <div className="mx-auto mt-12 grid w-full gap-6 sm:grid-cols-2 lg:w-3/4 lg:grid-cols-3 lg:items-stretch">
                    <PricingCard
                        title={t.pricingCards.plans.free.title}
                        price={t.pricingCards.plans.free.price}
                        description={t.pricingCards.plans.free.description}
                        features={t.pricingCards.plans.free.features}
                        buttonText={t.common.getStarted}
                        buttonVariant="outline"
                    />
                    <PricingCard
                        title={t.pricingCards.plans.pro.title}
                        price={proDisplay}
                        currencyLabel={currencyLabel}
                        pricePeriodLabel={periodLabel}
                        subtitle={`${t.pricingCards.payEvery} ${proSubtitleAmount} ${!yearly ? t.pricingCards.everyMonth : t.pricingCards.everyYear}`}
                        description={t.pricingCards.plans.pro.description}
                        features={t.pricingCards.plans.pro.features}
                        featuresLeadIn={t.pricingCards.everythingInFreePlus}
                        badge={t.pricingCards.plans.pro.badge}
                        buttonText={t.pricingCards.plans.pro.buttonText}
                    />
                    <PricingCard
                        title={t.pricingCards.plans.business.title}
                        price={businessDisplay}
                        currencyLabel={currencyLabel}
                        pricePeriodLabel={periodLabel}
                        subtitle={`${t.pricingCards.payEvery} ${businessSubtitleAmount} ${!yearly ? t.pricingCards.everyMonth : t.pricingCards.everyYear}`}
                        description={t.pricingCards.plans.business.description}
                        features={t.pricingCards.plans.business.features}
                        featuresLeadIn={t.pricingCards.everythingInProPlus}
                        buttonText={t.pricingCards.plans.business.buttonText}
                        buttonVariant="outline"
                    />
                </div>

                <div className="mt-20 lg:mt-32">
                    <div className="mx-auto max-w-5xl px-2 md:px-6">
                        <div className="max-w-lg md:mx-auto md:text-center">
                            <h2 className="text-foreground text-3xl font-semibold md:text-4xl">
                                {t.pricingCards.comparePlans}
                            </h2>
                        </div>

                        <div className="mx-auto mt-12 max-w-5xl">
                            <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1.9fr)] lg:gap-6">
                                <div>
                                    <div className="flex h-16 items-center">
                                        <div className="text-lg font-medium">{t.pricingCards.compareBenefits}</div>
                                    </div>
                                    {compareRows.map((row) => (
                                        <div
                                            key={row.name}
                                            className="flex min-h-14 items-center border-t border-border py-2 max-md:text-sm">
                                            <div>{row.name}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="-mx-2 overflow-x-auto pb-2 md:mx-0 md:overflow-visible md:pb-0">
                                    <div
                                        className="grid min-w-[min(100%,42rem)] grid-cols-3 rounded-2xl bg-linear-to-b from-foreground/3 p-4 [--color-border:color-mix(in_oklab,var(--color-foreground)6%,transparent)] md:min-w-0">
                                        <div>
                                            <div className="flex h-16 flex-col items-center justify-center gap-1 px-4 pt-2 text-center md:px-6">
                                                <Clover
                                                    aria-hidden
                                                    className="size-4"
                                                    strokeWidth={2}
                                                />
                                                <div className="text-sm font-medium">{t.pricingCards.planNames.free}</div>
                                            </div>
                                            {compareRows.map((row) => (
                                                <CompareCell
                                                    key={`free-${row.name}`}
                                                    included={row.free}
                                                />
                                            ))}
                                        </div>

                                        <div className="bg-card ring-foreground/5 rounded-lg shadow-lg shadow-black/10 ring-1">
                                            <div className="flex h-16 flex-col items-center justify-center gap-1 px-4 text-center md:px-6">
                                                <Gem
                                                    aria-hidden
                                                    className="size-4"
                                                    strokeWidth={2}
                                                />
                                                <div className="text-sm font-medium">{t.pricingCards.planNames.pro}</div>
                                            </div>
                                            {compareRows.map((row) => (
                                                <CompareCell
                                                    key={`pro-${row.name}`}
                                                    included={row.pro}
                                                />
                                            ))}
                                        </div>

                                        <div>
                                            <div className="flex h-16 flex-col items-center justify-center gap-1 px-4 text-center md:px-6">
                                                <Building2
                                                    aria-hidden
                                                    className="size-4"
                                                    strokeWidth={2}
                                                />
                                                <div className="text-sm font-medium">{t.pricingCards.planNames.business}</div>
                                            </div>
                                            {compareRows.map((row) => (
                                                <CompareCell
                                                    key={`biz-${row.name}`}
                                                    included={row.business}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </AnimatedGroup>
        </div>
    )
}
